// @file Vuex Store module for Post Actions menu (menu using the hamburger button on corner of the post)
/* eslint-disable no-param-reassign */
import { JsonAPIResource, JsonAPIResponse } from '@padlet/arvo'
import { copyToClipboard } from 'bits/clipboard'
import { __ } from 'bits/intl'
import PadletApi from 'surface/padlet_api'
import { sortPostsBySortIndex } from 'surface/sorter'
import { Cid, Post, Section } from 'types'
import { Module } from 'vuex'
import { copyPost, CopyXferPostParams, transferPost } from 'vuexstore/helpers/post'
import { GlobalSnackbarNotification, SnackbarNotificationType } from 'vuexstore/modules/global_snackbar'
import { CopyOrTransferPostResult, PostActionState, RootState } from 'vuexstore/surface/types'
const postActionModule: Module<PostActionState, RootState> = {
  namespaced: true,
  state: {
    // Only one media player is allowed at a time.
    currentlyPlayingPostCid: null,
    zoomedPostCid: null,
    zoomedLink: null,
    expandedPostCid: null,
    postUnderActionCid: null,
    postRightClickCoordinates: null,
    xTransferPostPanel: false,
    postBeingTransferredCid: null,
    transferPostActivePanel: 'main',
    xCopyPostPanel: false,
    postBeingCopiedCid: null,
    copyPostActivePanel: 'main',
    destinationWallSections: [],
    destinationWallSectionsFetched: false,
    copyOrTransferPostResult: null,
    copyOrTransferSectionId: null,
    xExpandedPostTextPanel: false,
    shouldFocusExpandedPostAddCommentInput: false,
  },
  getters: {
    zoomedPostCid: (state) => state.zoomedPostCid,
    zoomedLink: (state) => state.zoomedLink,
    expandedPostCid: (state) => state.expandedPostCid,
    expandedPost: (state, getters, rootState, rootGetters): Post | undefined => {
      return state.expandedPostCid && rootGetters['post/postEntitiesByCid'][state.expandedPostCid]
    },
    postUnderActionCid: (state) => state.postUnderActionCid,
    postRightClickCoordinates: (state) => state.postRightClickCoordinates,
    xTransferPostPanel: (state) => state.xTransferPostPanel,
    postBeingTransferredCid: (state) => state.postBeingTransferredCid,
    transferPostActivePanel: (state) => state.transferPostActivePanel,
    copyOrTransferPostResult: (state) => state.copyOrTransferPostResult,
    xCopyPostPanel: (state) => state.xCopyPostPanel,
    postBeingCopiedCid: (state) => state.postBeingCopiedCid,
    copyPostActivePanel: (state) => state.copyPostActivePanel,
    destinationWallSections: (state) => state.destinationWallSections,
    destinationWallSectionsFetched: (state) => state.destinationWallSectionsFetched,
    copyOrTransferSectionId: (state) => state.copyOrTransferSectionId,
    xExpandedPostTextPanel: (state) => state.xExpandedPostTextPanel,
    shouldFocusExpandedPostAddCommentInput: (state) => state.shouldFocusExpandedPostAddCommentInput,
    hasPostUnderActionWithConnections: (state, getters, rootState, rootGetters): boolean => {
      if (!getters.postUnderAction) return false
      const postId = getters.postUnderAction.id
      return (
        rootGetters['postConnection/connections'].filter((cxn): boolean => {
          return cxn.from_wish_id === postId || cxn.to_wish_id === postId
        }).length > 0
      )
    },
    xPostAction: (state, getters): boolean => {
      return (
        !!state.postUnderActionCid || !!state.expandedPostCid || getters.xTransferPostPanel || getters.xCopyPostPanel
      )
    },
    isPostUnderActionUserAuthored: (state, getters, rootState): boolean => {
      if (!getters.postUnderAction) return false
      return getters.postUnderAction.author_id === rootState.user.id
    },
    canEditPostUnderAction: (state, getters, rootState, rootGetters): boolean => {
      return rootGetters.canIEdit || (rootGetters.canIWrite && getters.isPostUnderActionUserAuthored)
    },
    canDeletePostUnderAction: (state, getters): boolean => getters.canEditPostUnderAction,
    canTransferPostUnderAction: (state, getters, rootState, rootGetters): boolean =>
      getters.canEditPostUnderAction && rootGetters.amIRegistered,
    canCopyPostUnderAction: (state, getters, rootState, rootGetters): boolean =>
      getters.canEditPostUnderAction && rootGetters.amIRegistered,
    canConnectPostUnderAction: (state, getters, rootState, rootGetters): boolean => {
      return (rootGetters.isCanvas || rootGetters.isMap) && rootGetters.canIWrite
    },
    canDisconnectPostUnderAction: (state, getters): boolean => {
      return getters.canConnectPostUnderAction && getters.hasPostUnderActionWithConnections
    },
    canChangePostStackOrder: (state, getters, rootState, rootGetters): boolean => {
      return (rootGetters.isCanvas || rootGetters.isMap) && rootGetters.canIEdit
    },
    doesPostUnderActionHaveAttachment: (state, getters): boolean => {
      return getters.postUnderAction && getters.postUnderAction.attachment && getters.postUnderAction.attachment !== ''
    },
    doesExpandedPostHaveAttachment: (state, getters): boolean => {
      return (
        getters.expandedPost &&
        !!(getters.expandedPost.wish_content?.attachment_props?.url || getters.expandedPost.attachment)
      )
    },
    canChangeCoverToPostUnderAction: (state, getters, rootState, rootGetters): boolean => {
      return (
        rootGetters.canIChangeCoverPost &&
        getters.doesPostUnderActionHaveAttachment &&
        getters.postUnderAction.published
      )
    },
    canChangeCoverToExpandedPost: (state, getters, rootState, rootGetters): boolean => {
      return rootGetters.canIChangeCoverPost && getters.doesExpandedPostHaveAttachment && getters.expandedPost.published
    },
    canChangePostUnderActionLocation: (_state, getters, _rootState, rootGetters): boolean => {
      return (
        rootGetters.isMap && (rootGetters.canIEdit || (rootGetters.canIWrite && getters.isPostUnderActionUserAuthored))
      )
    },
    isPostUnderActionPreviewPost: (state, getters, rootState, rootGetters): boolean => {
      return getters.postUnderAction && getters.postUnderAction.id === rootGetters.coverPostId
    },
    isExpandedPostPreviewPost: (state, getters, rootState, rootGetters): boolean => {
      return getters.expandedPost && getters.expandedPost.id === rootGetters.coverPostId
    },
    expandedPostServerId: (state, getters, rootState) => {
      return state.expandedPostCid && rootState.post.postEntitiesByCid[state.expandedPostCid]
        ? rootState.post.postEntitiesByCid[state.expandedPostCid].id
        : null
    },
    expandedPostFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/post${getters.expandedPostServerId}`
    },
    expandedPostMobileFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/post_v2/${getters.expandedPostServerId}`
    },
    expandedPostMobileBodyFrameUrl: (state, getters, rootState, rootGetters) => {
      if (!rootGetters.publicKey || !state.expandedPostCid) {
        return 'about:blank'
      }
      return `/padlets/${rootGetters.publicKey}/frames/body${getters.expandedPostServerId}`
    },
    expandedPostPostsToNavigate: (state, _getters, _rootState, rootGetters): Post[] => {
      let postsToNavigate: Post[] = []
      if (rootGetters.isShelf) {
        // If the padlet is in shelf format, we will navigate from first section
        // to the last, top post to the bottom.
        rootGetters['section/sortedSections'].forEach((section) => {
          const postCids = rootGetters['post/postCidsBySection'][section.id] || []
          const posts = postCids.map((cid) => rootGetters['post/postEntitiesByCid'][cid])
          postsToNavigate.push(...sortPostsBySortIndex(posts))
        })
      } else {
        postsToNavigate = rootGetters['post/sortedPosts']
      }
      return postsToNavigate
    },
    expandedPostPreviousPostCid: (_state, getters): Cid | null => {
      const currentPostIndex = getters.expandedPostPostsToNavigate.findIndex(
        (p: Post) => p.cid === getters.expandedPostCid,
      )
      const previousPostIndex = currentPostIndex - 1
      const previousPost = getters.expandedPostPostsToNavigate[previousPostIndex]
      return previousPost?.cid ?? null
    },
    expandedPostNextPostCid: (_state, getters): Cid | null => {
      const currentPostIndex = getters.expandedPostPostsToNavigate.findIndex(
        (p: Post) => p.cid === getters.expandedPostCid,
      )
      const nextPostIndex = currentPostIndex + 1
      const nextPost = getters.expandedPostPostsToNavigate[nextPostIndex]
      return nextPost?.cid ?? null
    },
    postUnderAction: (state, getters, rootState, rootGetters) => {
      if (!state.postUnderActionCid) return null
      const postEntitiesByCid = rootGetters['post/postEntitiesByCid']
      return postEntitiesByCid[state.postUnderActionCid]
    },
  },
  mutations: {
    SHOW_POST_ACTION_MENU(state, postCid) {
      state.postUnderActionCid = postCid
    },
    SET_POST_RIGHT_CLICK_COORDINATES(state, { postRightClickCoordinates }) {
      state.postRightClickCoordinates = postRightClickCoordinates
    },
    HIDE_POST_ACTION_MENU(state) {
      state.postUnderActionCid = null
      state.postRightClickCoordinates = null
    },
    ZOOM_POST(state, { zoomedPostCid, zoomedLink }): void {
      state.zoomedPostCid = zoomedPostCid
      state.zoomedLink = zoomedLink
    },
    UNZOOM_POST(state): void {
      state.zoomedPostCid = null
      state.zoomedLink = null
    },
    EXPAND_POST(state, postCid): void {
      state.expandedPostCid = postCid
    },
    UNEXPAND_POST(state): void {
      state.expandedPostCid = null
    },
    TOGGLE_EXPANDED_POST_TEXT_PANEL(state, status): void {
      if (status !== undefined) {
        state.xExpandedPostTextPanel = status
      } else {
        state.xExpandedPostTextPanel = !state.xExpandedPostTextPanel
      }
    },
    SET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT(state): void {
      state.shouldFocusExpandedPostAddCommentInput = true
    },
    UNSET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT(state): void {
      state.shouldFocusExpandedPostAddCommentInput = false
    },
    START_POST_TRANSFER(state, transferpostCid) {
      state.xTransferPostPanel = true
      state.postBeingTransferredCid = transferpostCid
    },
    END_POST_TRANSFER(state) {
      state.xTransferPostPanel = false
      state.postBeingTransferredCid = null
      state.transferPostActivePanel = 'main'
      state.copyOrTransferPostResult = null
    },
    UPDATE_POST_TRANSFER(state: PostActionState, copyXferPostResult: CopyOrTransferPostResult): void {
      if (copyXferPostResult.status !== 'error') state.postBeingTransferredCid = null
      state.copyOrTransferPostResult = copyXferPostResult
    },
    POST_TRANSFER_NAVIGATE(state, panel) {
      state.transferPostActivePanel = panel
    },
    START_POST_COPY(state, copypostCid) {
      state.xCopyPostPanel = true
      state.postBeingCopiedCid = copypostCid
    },
    END_POST_COPY(state) {
      state.xCopyPostPanel = false
      state.postBeingCopiedCid = null
      state.copyPostActivePanel = 'main'
      state.copyOrTransferPostResult = null
    },
    UPDATE_POST_COPY(state: PostActionState, copyXferPostResult: CopyOrTransferPostResult): void {
      state.copyOrTransferPostResult = copyXferPostResult
    },
    POST_COPY_NAVIGATE(state, panel) {
      state.copyPostActivePanel = panel
    },
    FETCH_DESTINATION_WALL_SECTIONS(state) {
      state.destinationWallSections = []
      state.destinationWallSectionsFetched = false
    },
    FETCHED_DESTINATION_WALL_SECTIONS(state, sections) {
      state.destinationWallSections = sections
      state.destinationWallSectionsFetched = true
    },
    SET_CURRENTLY_PLAYING_POST(state, cid): void {
      state.currentlyPlayingPostCid = cid
    },
    UNSET_CURRENTLY_PLAYING_POST(state, cid): void {
      if (state.currentlyPlayingPostCid === cid) {
        state.currentlyPlayingPostCid = null
      }
    },
    SET_COPY_OR_TRANSFER_SECTION_ID(state, sectionId): void {
      state.copyOrTransferSectionId = sectionId
    },
  },
  actions: {
    /* ------------
     * SURFACE STATE MODIFICATION OPERATIONS
     * ------------ */
    showPostActionMenu({ commit }, { postCid }) {
      commit('SHOW_POST_ACTION_MENU', postCid)
    },
    setPostRightClickCoordinates({ commit }, { postRightClickCoordinates }) {
      commit('SET_POST_RIGHT_CLICK_COORDINATES', { postRightClickCoordinates })
    },
    hidePostActionMenu({ commit }) {
      commit('HIDE_POST_ACTION_MENU')
    },
    zoomPost({ commit }, { zoomedPostCid, zoomedLink }) {
      commit('UNEXPAND_POST')
      commit('SET_CURRENTLY_PLAYING_POST', zoomedPostCid)
      commit('ZOOM_POST', { zoomedPostCid, zoomedLink })
    },
    unzoomPost({ commit, state }) {
      commit('UNEXPAND_POST')
      commit('UNSET_CURRENTLY_PLAYING_POST', state.zoomedPostCid)
      commit('UNZOOM_POST')
    },
    expandPost(
      { commit, rootState, state },
      { postCid, xTextPanel, shouldFocusInput }: { postCid: Cid; xTextPanel?: boolean; shouldFocusInput?: boolean },
    ): void {
      commit('UNSET_CURRENTLY_PLAYING_POST', state.zoomedPostCid)
      commit('UNZOOM_POST')
      if (!rootState.isApp) {
        commit('SET_CURRENTLY_PLAYING_POST', postCid)
      }
      commit('EXPAND_POST', postCid)
      if (xTextPanel !== undefined) {
        commit('TOGGLE_EXPANDED_POST_TEXT_PANEL', xTextPanel)
      }
      // Only focus input if text panel is also open
      if (xTextPanel && shouldFocusInput) {
        commit('SET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT')
      }
    },
    unexpandPost({ commit, state }) {
      commit('UNEXPAND_POST')
      commit('UNSET_CURRENTLY_PLAYING_POST', state.expandedPostCid)
      commit('UNSET_SHOULD_FOCUS_EXPANDED_POST_ADD_COMMENT_INPUT')
    },
    toggleExpandedPostTextPanel({ commit }, payload?: { status: boolean }): void {
      let status: boolean | undefined = undefined
      if (payload && payload.status !== undefined) status = payload.status
      commit('TOGGLE_EXPANDED_POST_TEXT_PANEL', status)
    },
    startPostTransfer({ commit }, { transferPostCid }) {
      commit('START_POST_TRANSFER', transferPostCid)
    },
    endPostTransfer({ commit }) {
      commit('END_POST_TRANSFER')
    },
    actuallyTransferPost({ dispatch, commit, rootGetters }, params: CopyXferPostParams): void {
      const post = { id: params.postId }
      const method = (): Promise<void> => transferPost({ dispatch, rootGetters }, params)
      const customRequest = { key: 'TRANSFER_POST', method }
      commit('SET_COPY_OR_TRANSFER_SECTION_ID', params.wallSectionId)
      dispatch('post/enqueueRequest', { post, customRequest }, { root: true })
    },
    async updatePostTransferred(
      { commit, dispatch, rootGetters, rootState },
      { transferredPostCid, error, newPostData, isDestinationNewWall, isDestinationThisWall },
    ) {
      const result: CopyOrTransferPostResult = {
        status: 'success',
        operation: 'transfer',
        isDestinationThisWall,
        isDestinationNewWall,
      }
      if (error) {
        result.status = 'error'
        result.text = __('Sorry, we failed to transfer your post.') + ' ' + (error.error || '')
      } else {
        let wallInfo = rootState.wall
        if (!isDestinationThisWall) {
          wallInfo = await PadletApi.Wall.read({ id: newPostData.wall_id })
        } else {
          dispatch('post/updatePostInStore', newPostData, { root: true })
        }
        result.url = wallInfo.links.show
        result.text = __('Post transferred successfully.')
      }
      commit('UPDATE_POST_TRANSFER', result)
    },
    postTransferNavigate({ commit }, { panel }) {
      commit('POST_TRANSFER_NAVIGATE', panel)
    },
    startPostCopy({ commit }, { copyPostCid }) {
      commit('START_POST_COPY', copyPostCid)
    },
    endPostCopy({ commit }) {
      commit('END_POST_COPY')
    },
    actuallyCopyPost({ dispatch, commit }, params: CopyXferPostParams): void {
      if (params.wallSectionId) {
        commit('SET_COPY_OR_TRANSFER_SECTION_ID', params.wallSectionId)
      }
      const post = { id: params.postId }
      const method = (): Promise<void> => copyPost({ dispatch }, params)
      const customRequest = { key: 'COPY_POST', method }
      dispatch('post/enqueueRequest', { post, customRequest }, { root: true })
    },
    async updatePostCopied(
      { commit, rootGetters, rootState, dispatch },
      { error, newPostData, isDestinationNewWall },
    ): Promise<void> {
      const result: CopyOrTransferPostResult = {
        status: 'success',
        operation: 'copy',
        isDestinationThisWall: false,
        isDestinationNewWall,
      }
      if (error) {
        result.status = 'error'
        result.text = __('Sorry, we failed to copy your post.') + ' ' + (error.error || '')
      } else {
        result.isDestinationThisWall = !isDestinationNewWall && rootGetters.wallId === newPostData.wall_id
        let wallInfo = rootState.wall
        if (result.isDestinationThisWall) {
          dispatch('addPostCopiedOrMovedToCurrent', { newPostData })
        } else {
          wallInfo = await PadletApi.Wall.read({ id: newPostData.wall_id })
        }
        result.url = wallInfo.links.show
        result.text = __('Post copied successfully.')
      }
      commit('UPDATE_POST_COPY', result)
    },
    addPostCopiedOrMovedToCurrent({ dispatch }, { newPostData }) {
      dispatch('addPost', { post: newPostData }, { root: true })
    },
    postCopyNavigate({ commit }, { panel }) {
      commit('POST_COPY_NAVIGATE', panel)
    },
    async fetchDestinationWallSections({ commit }, { wallId }) {
      commit('FETCH_DESTINATION_WALL_SECTIONS')
      const sectionFetchedData: JsonAPIResponse<Section> = await PadletApi.WallSection.readAll({
        wall: {
          id: wallId,
        },
      })
      const sections = (sectionFetchedData.data as JsonAPIResource<Section>[]).map((section) => section.attributes)
      commit('FETCHED_DESTINATION_WALL_SECTIONS', sections)
    },
    setCurrentlyPlayingPost({ commit }, { cid }): void {
      commit('SET_CURRENTLY_PLAYING_POST', cid)
    },
    unsetCurrentlyPlayingPost({ commit }, { cid }): void {
      commit('UNSET_CURRENTLY_PLAYING_POST', cid)
    },
    confirmDeletePost({ dispatch }, { postCid }: { postCid: Cid }): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          title: __('Delete post?'),
          body: __('Are you sure you want to delete this post? This cannot be undone!'),
          confirmButtonText: __('Delete'),
          cancelButtonText: __('Nevermind'),
          isConfirmActionDanger: true,
          afterConfirmActions: [{ type: 'postAction/unexpandPost' }, { type: 'post/deletePost', payload: { postCid } }],
        },
        { root: true },
      )
    },
    copyPostLinkAndAlert({ dispatch, rootGetters }, { postCid }: { postCid: Cid }): void {
      const post = rootGetters['post/getPostByCid'](postCid)
      if (post && post.permalink) {
        copyToClipboard(post.permalink)
        dispatch(
          'snackbar',
          {
            notificationType: SnackbarNotificationType.success,
            message: __('Link copied to clipboard'),
          },
          { root: true },
        )
      }
    },
    copyAttachmentLinkAndAlert({ dispatch, rootGetters }, { postCid }: { postCid: Cid }): void {
      const post = rootGetters['post/getPostByCid'](postCid)
      const url = post?.wish_content?.attachment_props?.url || post?.attachment
      if (url) {
        copyToClipboard(url)
        dispatch(
          'snackbar',
          {
            notificationType: SnackbarNotificationType.success,
            message: __('Link copied to clipboard'),
          },
          { root: true },
        )
      }
    },
    /* ------------
     * REMOTE
     * ------------ */
    deletePostRemote({ state, getters, dispatch, rootGetters }, post): void {
      if (post) {
        const postFromStore = rootGetters['post/getLiveOrDeletedPost'](post)
        if (!postFromStore) return

        const postCid = postFromStore.cid
        if (postCid === state.postBeingTransferredCid) {
          dispatch('endPostTransfer')
        }
        if (postCid === state.postBeingCopiedCid) {
          dispatch('endPostCopy')
        }
        if (postCid === state.postUnderActionCid) {
          dispatch('hidePostActionMenu')
        }
        if (postCid === state.zoomedPostCid) {
          dispatch('unzoomPost')
        }
        if (postCid === state.expandedPostCid) {
          // When the expanded post is deleted, show snackbar and navigate to the next post.
          const snackbarPayload: Partial<GlobalSnackbarNotification> = {
            message: __('Previous post has been deleted elsewhere.'),
          }
          dispatch('snackbar', snackbarPayload, { root: true })
          const postIndex = getters.expandedPostPostsToNavigate.findIndex(
            (p: Post): boolean => p.cid === state.expandedPostCid,
          )
          if (getters.expandedPostPostsToNavigate.length === 1) {
            dispatch('unexpandPost')
          } else if (postIndex === getters.expandedPostPostsToNavigate.length - 1) {
            // go to previous post
            dispatch('expandPost', { postCid: getters.expandedPostPostsToNavigate[postIndex - 1].cid })
          } else {
            // go to next post
            dispatch('expandPost', { postCid: getters.expandedPostPostsToNavigate[postIndex + 1].cid })
          }
        }
      }
    },
    // TODO we should not need this method, ideally. Getters and watches should take care of this.
    recalibrate({ state, rootState, dispatch }): void {
      const postEntitiesByCid = rootState.post.postEntitiesByCid
      if (state.postBeingTransferredCid && !(state.postBeingTransferredCid in postEntitiesByCid)) {
        dispatch('endPostTransfer')
      }
      if (state.postBeingCopiedCid && !(state.postBeingCopiedCid in postEntitiesByCid)) {
        dispatch('endPostCopy')
      }
      if (state.postUnderActionCid && !(state.postUnderActionCid in postEntitiesByCid)) {
        dispatch('hidePostActionMenu')
      }
      if (state.zoomedPostCid && !(state.zoomedPostCid in postEntitiesByCid)) {
        dispatch('unzoomPost')
      }
      if (state.expandedPostCid && !(state.expandedPostCid in postEntitiesByCid)) {
        dispatch('unexpandPost')
      }
    },
  },
}

export default postActionModule
